/**
 * Shared JSON merging utilities for all loaders
 * Pure ES Module version
 *
 * Port of C# JsonMergeUtil class with exact behavior
 */

import { TemplateOperation, JsonArrayPlaceholder, JsonKeyPlaceholder } from '../../model/index.js';
import { JsonObject } from '../../app/json/jsonValue.js';

// Import from split files
import { buildMergedJson } from './jsonMergeUtil_Conversion.js';
import {
    applySkipArrayMarkers,
    applySkipConditionalMarkers,
    applyStandaloneConditionals,
    buildArrayMetadataLookup,
    collectArrayBlocksInTemplate,
    processJsonArrayBlocks,
    processSimplePlaceholders,
    replaceMissingArrayBlocks
} from './jsonMergeUtil_MergeHelpers.js';

// Re-export utilities for external use
export { indexOfCaseInsensitive, lastIndexOfCaseInsensitive, replaceAllCaseInsensitive } from './jsonMergeUtil_Replace.js';
export { buildMergedJson } from './jsonMergeUtil_Conversion.js';
export { handleConditional } from './jsonMergeUtil_Conditional.js';
export { canOverrideKey } from './jsonMergeUtil_OverridePermissions.js';

/**
 * Escape marker: Unicode Zero Width Space (U+200B) inserted between {{ to prevent
 * Normal/PreProcess engines from re-interpreting data values as component placeholders.
 * Must be unescaped after all template processing is complete.
 */
const DATA_ESCAPE_MARKER = '\u200B';
const ESCAPED_DOUBLE_BRACE = '{\u200B{';

class JsonMergeUtil {
    /**
     * Removes escape markers from final output after all template processing is complete.
     * Called by engines (Normal, PreProcess) at the end of MergeTemplates.
     */
    static unescapeDataValues(html: string): string {
        if (html.includes(DATA_ESCAPE_MARKER)) {
            return html.replace(/\{\u200B\{/g, '{{');
        }
        return html;
    }

    static executeWithReadOnlyInputs<T>(
        contextJson: JsonObject | null,
        configJson: JsonObject | null,
        action: () => T
    ): T {
        const contextWasReadOnly = contextJson?.isReadOnly ?? false;
        const configWasReadOnly = configJson?.isReadOnly ?? false;

        if (contextJson) {
            contextJson.isReadOnly = true;
        }
        if (configJson) {
            configJson.isReadOnly = true;
        }

        try {
            return action();
        } finally {
            if (contextJson) {
                contextJson.isReadOnly = contextWasReadOnly;
            }
            if (configJson) {
                configJson.isReadOnly = configWasReadOnly;
            }
        }
    }

    // [Dafny Verified] Properties M1-M7 — Verification/dafny/src/MergeTemplateWithJson.dfy
    /**
     * Merges HTML template with JSON data using placeholder replacement
     * Handles:
     * - Array blocks: {{@array}}...{{/array}}
     * - Conditional blocks: {{?condition}}...{{/condition}}
     * - Empty array blocks: {{^array}}...{{/array}}
     * - Simple placeholders: {{$key}}
     */
    static mergeTemplateWithJson(
        template: string,
        jsonObject: JsonObject,
        templateKey: string = '',
        currentTemplateName: string = '',
        operations: TemplateOperation[] | null = null,
        maxArrayOperationItems: number = 1,
        defaultReplaceHtml: string = '*****',
        defaultSkipJson: string = '',
        arrayMetadata: JsonArrayPlaceholder[] | null = null,
        jsonKeyMetadata: JsonKeyPlaceholder[] | null = null,
        contextJson: JsonObject | null = null,
        configJson: JsonObject | null = null
    ): string {
        // Early exit: if template has no JSON placeholders, skip all processing
        // Markers: {{$ (value), {{@ (array), {{? (conditional), {{! (skip), {{^ (inverse condition)
        if (!template.includes('{{$') && !template.includes('{{@') &&
            !template.includes('{{?') && !template.includes('{{!') && !template.includes('{{^')) {
            return template;
        }

        return JsonMergeUtil.executeWithReadOnlyInputs(contextJson, configJson, () => {
            // Build merged working JsonObject, applying context and config override rules.
            const merged = buildMergedJson(jsonObject, contextJson, configJson);

            let result = template;

            result = applySkipArrayMarkers(result, templateKey, currentTemplateName, operations, defaultSkipJson);

            result = applySkipConditionalMarkers(result, templateKey, currentTemplateName, operations, defaultSkipJson);

            const arrayBlocksInTemplate = collectArrayBlocksInTemplate(result, arrayMetadata);
            const metadataLookup = buildArrayMetadataLookup(arrayMetadata);

            result = processJsonArrayBlocks(result, merged, arrayBlocksInTemplate, metadataLookup, templateKey, currentTemplateName, operations, maxArrayOperationItems, defaultReplaceHtml, defaultSkipJson);

            result = replaceMissingArrayBlocks(result, merged, arrayBlocksInTemplate, templateKey, currentTemplateName, operations, defaultReplaceHtml);

            result = processSimplePlaceholders(result, merged, templateKey, currentTemplateName, operations, defaultReplaceHtml, defaultSkipJson, jsonKeyMetadata, arrayMetadata);

            result = applyStandaloneConditionals(result, merged, templateKey, currentTemplateName, operations);

            return result;
        });
    }

}

export { JsonMergeUtil, DATA_ESCAPE_MARKER, ESCAPED_DOUBLE_BRACE };
export const mergeTemplateWithJson = JsonMergeUtil.mergeTemplateWithJson.bind(JsonMergeUtil);
